import {createContext, use, useCallback, useState, type ReactNode} from 'react'
import {createStore, useStore} from 'zustand'
import {devtools} from 'zustand/middleware'

import {useIsDataRouterEnabled} from '@github-ui/react-core/future/use-is-data-router-enabled'

import {getDiffSizeCategory, type DiffSizeCategory} from '../helpers/get-diff-size-category'
import type {DiffstatData} from '../page-data/payloads/header'
import {DiffstatRouterDataBridge} from '../page-data/loaders/use-diffstat-data'

export type PRAnalyticsInitialStateData = {
  diffVersion: 'v1' | 'v2'
  virtualizationEnabled: boolean
}
type State = Pick<PRAnalyticsInitialStateData, 'diffVersion'> & {
  diffSizeCategory: DiffSizeCategory
  virtualizationEnabled: 'true' | 'false'
  getINPDiffTags: () => {
    diffSizeCategory: DiffSizeCategory
    diffVersion: 'v1' | 'v2'
    virtualizationEnabled: 'true' | 'false'
  }
}
export type PullRequestAnalyticsStore = ReturnType<typeof createPullRequestAnalyticsStore>

const PullRequestAnalyticsStoreContext = createContext<PullRequestAnalyticsStore | null>(null)

export function usePullRequestAnalyticsStore<T>(selector: (state: State) => T): T {
  const store = use(PullRequestAnalyticsStoreContext)
  if (!store) throw new Error('Missing PullRequestAnalyticsStoreProvider in the tree')
  return useStore(store, selector)
}

type PullRequestAnalyticsStoreProviderProps = {
  children?: ReactNode
  initData: PRAnalyticsInitialStateData
}
export const PullRequestAnalyticsStoreProvider = ({initData, children}: PullRequestAnalyticsStoreProviderProps) => {
  const [store] = useState(() => createPullRequestAnalyticsStore(initData))
  const isDataRouter = useIsDataRouterEnabled()

  const handleDiffstat = useCallback(
    (diffstat: DiffstatData['diffstat']) => {
      if (diffstat) {
        store.setState({diffSizeCategory: getDiffSizeCategory(diffstat.linesChanged)})
      }
    },
    [store],
  )

  return (
    <PullRequestAnalyticsStoreContext value={store}>
      {/* The diffstat lives in the data-router route query, so only read it when mounted inside a data router. */}
      {isDataRouter && <DiffstatRouterDataBridge onDiffstat={handleDiffstat} />}
      {children}
    </PullRequestAnalyticsStoreContext>
  )
}

const createPullRequestAnalyticsStore = (initStateData: PRAnalyticsInitialStateData) => {
  return createStore<State>()(
    devtools(
      (_, get) => ({
        ...initStateData,
        virtualizationEnabled: initStateData.virtualizationEnabled === true ? 'true' : 'false',
        diffSizeCategory: 'unknown',
        getINPDiffTags: () => ({
          diffVersion: get().diffVersion,
          diffSizeCategory: get().diffSizeCategory,
          virtualizationEnabled: get().virtualizationEnabled,
        }),
      }),
      {name: 'analytics-store'},
    ),
  )
}
